import { StyleSheet, css } from 'aphrodite'
import type { Invoice } from '../../types/invoice'

const InvoiceDisplay: React.FC<{ invoice: Invoice; logoUrl?: string }> = ({
  invoice,
  logoUrl,
}) => {
  return (
    <div className={css(styles.invoiceContainer)}>
      <div className={css(styles.header)}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <div className={css(styles.logoSection)}>
            {logoUrl ? (
              <img
                src={logoUrl}
                alt="Company Logo"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  borderRadius: 6,
                }}
              />
            ) : (
              <div className={css(styles.logoPlaceholder)}>
                <i
                  className="fa-regular fa-image"
                  style={{ fontSize: 32, color: '#9CA3AF' }}
                />
              </div>
            )}
          </div>
        </div>
        <h1 className={css(styles.invoiceTitle)}>INVOICE</h1>
      </div>
      <div className={css(styles.billingSection)}>
        <div className={css(styles.leftSection)}>
          <div className={css(styles.companySection)}>
            <div className={css(styles.companyHeader)}>Your Company:</div>
            <div className={css(styles.text)}>{invoice.company_name}</div>
            <div className={css(styles.text)}>{invoice.company_address}</div>
          </div>
          <div className={css(styles.billTo)}>
            <div className={css(styles.billToHeader)}>Bill To:</div>
            <div className={css(styles.text)}>
              {/* Prefer snapshot, fallback to FK for backward compatibility */}
              {invoice.bill_to_first_name
                ? `${invoice.bill_to_first_name} ${invoice.bill_to_last_name || ''}`.trim()
                : `${invoice.bill_to?.first_name || ''} ${invoice.bill_to?.last_name || ''}`.trim()}
            </div>
            <div className={css(styles.text)}>
              {invoice.bill_to_email || invoice.bill_to?.email}
            </div>
          </div>
        </div>
        <div className={css(styles.invoiceInfo)}>
          <div className={css(styles.invoiceInfoHeader)}>Invoice Details</div>
          <div className={css(styles.infoRow)}>
            <span className={css(styles.infoLabel)}>Invoice Date</span>
            <div className={css(styles.text)}>{invoice.invoice_date}</div>
          </div>
          <div className={css(styles.infoRow)}>
            <span className={css(styles.infoLabel)}>Due Date</span>
            <div className={css(styles.text)}>{invoice.due_date}</div>
          </div>
          {invoice.purchase_order_number && (
            <div className={css(styles.infoRow)}>
              <span className={css(styles.infoLabel)}>PO #</span>
              <div className={css(styles.text)}>
                {invoice.purchase_order_number}
              </div>
            </div>
          )}
          {invoice.payment_terms_text && (
            <div className={css(styles.infoRow)}>
              <span className={css(styles.infoLabel)}>Payment Terms</span>
              <div className={css(styles.text)}>
                {invoice.payment_terms_text}
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Additional Details */}
      {(invoice.summary_description || invoice.how_to_pay) && (
        <div className={css(styles.additionalSection)}>
          {invoice.summary_description && (
            <div className={css(styles.detailBlock)}>
              <div className={css(styles.detailHeader)}>
                Summary Description
              </div>
              <div className={css(styles.text)}>
                {invoice.summary_description}
              </div>
            </div>
          )}
          {invoice.how_to_pay && (
            <div className={css(styles.detailBlock)}>
              <div className={css(styles.detailHeader)}>How to Pay</div>
              <div className={css(styles.text)}>{invoice.how_to_pay}</div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const styles = StyleSheet.create({
  invoiceContainer: {
    backgroundColor: 'white',
    padding: '40px',
    borderTopLeftRadius: '8px',
    borderTopRightRadius: '8px',
    borderBottomLeftRadius: '0px',
    borderBottomRightRadius: '0px',
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    maxWidth: '90%',
    margin: '0 auto',
    '@media (max-width: 1024px)': {
      padding: '16px',
      maxWidth: '100%',
    },
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '40px',
    '@media (maxWidth: 1024px)': {
      flexDirection: 'column',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '20px',
    },
  },
  logoSection: {
    width: '200px',
    height: '100px',
    marginBottom: '24px',
    '@media (max-width: 1024px)': {
      width: '140px',
      height: '70px',
      marginBottom: '8px',
    },
  },
  logoPlaceholder: {
    width: 200,
    height: 100,
    border: '2px dashed #D1D5DB',
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F9FAFB',
    overflow: 'hidden',
    position: 'relative',
    padding: 0,
  },
  invoiceTitle: {
    fontSize: '36px',
    fontWeight: 700,
    color: '#111827',
    margin: 0,
    padding: '16px',
    '@media (max-width: 1024px)': {
      fontSize: '24px',
      padding: 0,
      textAlign: 'center',
    },
  },
  billingSection: {
    display: 'grid',
    gridTemplateColumns: '3fr 2fr',
    gap: '40px',
    marginBottom: '40px',
    '@media (max-width: 1024px)': {
      gridTemplateColumns: '1fr',
      gap: '16px',
      marginBottom: '20px',
    },
  },
  leftSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '32px',
    '@media (max-width: 1024px)': {
      gap: '16px',
    },
  },
  companySection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  billTo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  billToHeader: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '8px',
  },
  invoiceInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    backgroundColor: '#F9FAFB',
    padding: '24px',
    borderRadius: '8px',
    height: 'fit-content',
    '@media (max-width: 1024px)': {
      padding: '16px',
    },
  },
  invoiceInfoHeader: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '8px',
  },
  infoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    '@media (max-width: 1024px)': {
      gap: '8px',
    },
  },
  infoLabel: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
    width: '100px',
    '@media (max-width: 1024px)': {
      fontSize: '13px',
      width: '80px',
    },
  },
  text: {
    fontSize: '14px',
    color: '#374151',
    '@media (max-width: 1024px)': {
      fontSize: '13px',
    },
  },
  companyHeader: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '8px',
    '@media (max-width: 1024px)': {
      fontSize: '13px',
      marginBottom: '4px',
    },
  },
  additionalSection: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '24px',
    marginBottom: '24px',
    '@media (max-width: 1024px)': {
      gridTemplateColumns: '1fr',
      gap: '16px',
    },
  },
  detailBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  detailHeader: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#374151',
  },
})

export default InvoiceDisplay
